import { useMemo, useRef, useState } from "react";

import { providerPortalB2cConfig } from "../auth/authProviders";
import { ropcLogin } from "../auth/b2cClient";
import {
  EXT_SIGNIN_PROVIDER,
  postSessionToOpener,
} from "../auth/extSignInHandoff";
import B2cForm from "./login/B2cForm";

/**
 * Top-level sign-in window opened by the side-panel iframe (see
 * auth/extSignInHandoff.ts). It renders the SAME B2cForm the web login uses,
 * but as a top-level document, which is what lets Chrome's built-in password
 * manager and third-party managers fill and save.
 *
 * Deliberately rendered by App.tsx OUTSIDE the router / MSAL / AuthProvider /
 * SessionGuard / Apollo tree: it must never boot the normal app, redirect to
 * /members once a session exists in this window, or touch the tab's session.
 *
 * Tokens are obtained with `persist: false`: they go to the opener via
 * postMessage ONLY. Not clearing afterwards (rather than not writing) matters
 * because this window's localStorage is the app's REAL, unpartitioned storage
 * — clearing would wipe a legitimate session the user has in a normal tab.
 */

/** Keep the window open briefly after success so the browser's "save password"
 * prompt (triggered by B2cForm's credentials.store) isn't torn down with it. */
const CLOSE_DELAY_MS = 5000;

export default function ExtSignInPopupPage() {
  const nonce = useMemo(
    () => new URLSearchParams(window.location.search).get("nonce"),
    [],
  );
  const cfg = useMemo(() => providerPortalB2cConfig(), []);
  const [done, setDone] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const opener = window.opener as Window | null;

  let problem: string | null = null;
  if (!opener || !nonce) {
    problem =
      "This window must be opened from the Atlas Copilot side panel. Close it and use Sign in there.";
  } else if (!cfg) {
    problem = "Atlas account sign-in is not configured for this environment.";
  }

  async function handleSubmit(email: string, password: string) {
    if (!opener || !nonce || !cfg) return;
    const tokens = await ropcLogin(cfg, email, password, { persist: false });
    postSessionToOpener(
      opener,
      nonce,
      EXT_SIGNIN_PROVIDER,
      tokens,
      window.location.origin,
    );
    setDone(true);
    closeTimer.current = setTimeout(() => window.close(), CLOSE_DELAY_MS);
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 font-sans text-foreground bg-gradient-to-br from-gray-50 via-white to-gray-100/60 dark:from-background dark:via-background dark:to-background">
      <div className="w-full max-w-sm bg-card text-card-foreground rounded-2xl border border-border/60 p-8 flex flex-col items-center gap-6 shadow-lg">
        <h1 className="text-2xl font-bold m-0">Atlas Copilot</h1>
        {problem ? (
          <p role="alert" className="text-sm text-destructive font-medium m-0 text-center">
            {problem}
          </p>
        ) : done ? (
          <div role="status" className="flex flex-col items-center gap-3 text-center">
            <p className="text-sm font-semibold m-0">You&apos;re signed in.</p>
            <p className="text-xs text-muted-foreground m-0">
              If your browser offers to save the password, choose Save. This
              window closes automatically.
            </p>
            <button
              type="button"
              onClick={() => {
                if (closeTimer.current) clearTimeout(closeTimer.current);
                window.close();
              }}
              className="text-sm font-medium text-primary hover:underline"
            >
              Close now
            </button>
          </div>
        ) : (
          <B2cForm variant="primary" onSubmit={handleSubmit} />
        )}
      </div>
    </div>
  );
}
